import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { registerFeatures } from './features-registration';
import { initializeUIPreferences } from './stores/use-ui-prefs-store';
import { installChunkReloadGuard } from './router/chunk-load-error';
import { renderBootFailure } from './boot-failure';
import { validateEnv } from '@/lib/env';
import '@/shared/styles/globals.css';
// Side-effect import : déclenche le bloc d'auto-init module-level qui ré-applique
// les custom colors si l'utilisateur les avait activés (anti-FOUC). Sans cet
// import, le bloc ne tournerait qu'à la première importation par SettingsFormView.
import '@/app/stores/use-custom-colors-store';

// Toutes les étapes du démarrage passent par `start()`, et son échec par
// `renderBootFailure` : sans ça, un rejet laissait une page blanche muette
// (SAAS-898). Les étapes anti-FOUC restent synchrones et avant le mount.
async function start(): Promise<void> {
  // 0. Fail-fast sur les vars d'env critiques (URL gateway invalide, etc.).
  validateEnv();

  // 0bis. Un onglet ouvert pendant un redéploiement réclame des fichiers qui
  //       n'existent plus : on recharge une fois pour récupérer la nouvelle
  //       version, au lieu d'une page d'erreur (SAAS-984).
  installChunkReloadGuard();

  // 1. Applique les préférences UI persistées AVANT le mount React (anti-FOUC).
  initializeUIPreferences();

  await bootstrap();
}

// 2. Enregistre toutes les features dans le registry, puis monte React.
//    `registerFeatures()` est async (registration dev-only en import dynamique) ;
//    on attend sa résolution avant `render()` pour que `createRouter()` voie
//    toutes les features.
async function bootstrap(): Promise<void> {
  await registerFeatures();

  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element #root introuvable dans index.html');
  }

  createRoot(rootEl).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

start().catch((error: unknown) => {
  renderBootFailure(error);
});
